import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import Axios from 'axios';
import { FaCog, FaFileExcel } from 'react-icons/fa';
import FileSaver from 'file-saver';
import intlShape from '@/shared/utils/intlShape';

import { CanUpdate, showWhenPermission } from '@/shared/components/security';
import ScrollableTable from '@/shared/components/table/ScrollableTable';
import api from '@/shared/api';
import ColumnHeader from '@/shared/components/table/ColumnHeader';
import Checkbox from '@/shared/components/input/Checkbox';
import config from '@/config';
import handleHttpError from '@/shared/services/HandleHttpError';
import ToastrService from '@/shared/services/ToastrService';
import SignalRService from '@/shared/services/SignalRService';
import { injectStateService, StateService } from '@/shared/services/StateService';
import CheckboxListModal from '@/shared/components/CheckboxListModal';
import {
  handleHiddenColumnsSelection,
  initHiddenColumnsState,
} from '@/shared/utils/hiddenColumnsHelper';
import SimplePanel from '@/shared/components/SimplePanel';
import ZendeskHelp from '@/shared/components/ZendeskHelp';
import { hasAssignments } from '@/shared/components/security/HasAssignments';
import Button from '@/shared/components/Button';
import utils from '@/shared/utils/adminpulse';
import AuthService, { functionalities } from '@/shared/services/AuthService';
import SendEmailModal from '@/shared/components/email/MultiRelationsMailModal';
import BatchRegistrationModal from '@/shared/components/registration/BatchRegistrationModal';
import GlobalActionsButton from './GlobalActionsButton';
import PlannedRegistrationOverviewFilters, {
  defaultFilters,
} from './PlannedRegistrationOverviewFilters';
import PlannedRegistrationOverviewRow from './PlannedRegistrationOverviewRow';
import messages from './messages';
import CreatePlannedRegistrationsModal from './modals/CreatePlannedRegistrationsModal';
import ChangePlannedRegistrationUserModal from './modals/ChangePlannedRegistrationUserModal';
import YesNoModal from '../../../shared/components/YesNoModal';

const columnSelectionStorageKey = 'PlannedRegistrationOverviewColumns';
const storageKey = 'PlannedRegistrationOverviewParams';

const columnDefinitions = [
  { id: 'relationUniqueIdentifier', message: messages.UniqueIdentifier, visibleByDefault: false },
  { id: 'relationCode', message: messages.RelationCode, visibleByDefault: false },
  { id: 'user', message: messages.User, visibleByDefault: true },
];

class PlannedRegistrationOverviewContainer extends Component {
  hasPriceListPermission = AuthService.hasPermission({ functionality: functionalities.Pricelist });

  constructor(props) {
    super(props);

    const { overviewFilters: sessionFilters = {}, ...sessionParams } =
      JSON.parse(sessionStorage.getItem(storageKey)) || {};
    this.state = {
      rows: [],
      total: 0,
      allSelected: false,
      plannedRegistrations: [],
      initialSearchParams: {
        page: 0,
        pageSize: 50,
        sortKey: 'relation.displayName',
        desc: false,
        overviewFilters: {
          ...defaultFilters,
          ...sessionFilters,
        },
        ...sessionParams,
      },
      showCreatePlannedRegistrationsModal: false,
      showRemovePlannedRegistrationsModal: false,
      showChangeUserModal: false,
      showSendMailModal: false,
      showCreateRegistrationModal: false,
      relationLanguages: null,
      showColumnSelectionModal: false,
      columnStatus: {},
    };
    const { hideableColumns, columnStatus } = initHiddenColumnsState(
      columnSelectionStorageKey,
      columnDefinitions,
      props.intl,
    );
    this.state.hideableColumns = hideableColumns;
    this.state.columnStatus = columnStatus;
  }

  componentDidMount() {
    SignalRService.subscribe('jobUpdated', this.jobUpdateHandler);
  }

  componentWillUnmount() {
    SignalRService.unsubscribe('jobUpdated', this.jobUpdateHandler);
    if (this.searchToken && !this.searchToken.token.reason) {
      this.searchToken.cancel('Operation canceled due to leaving page.');
    }
  }

  filtersChanged = filters => {
    this.setState(state => ({
      ...state,
      initialSearchParams: {
        ...state.initialSearchParams,
        page: 0,
        overviewFilters: filters,
      },
    }));
  };

  loadItems = searchParams => {
    sessionStorage.setItem(
      storageKey,
      JSON.stringify({
        desc: searchParams.desc,
        sortKey: searchParams.sortKey,
        overviewFilters: searchParams.overviewFilters,
      }),
    );
    if (this.searchToken && !this.searchToken.token.reason) {
      this.searchToken.cancel('Operation canceled due to new request.');
    }
    this.searchToken = Axios.CancelToken.source();
    return api.base
      .get(`${config.appUrls.api}/api/PlannedRegistration/Overview`, {
        params: {
          desc: searchParams.desc,
          sort: searchParams.sortKey,
          page: searchParams.page,
          take: searchParams.pageSize,
          ...searchParams.overviewFilters,
        },
        cancelToken: this.searchToken.token,
      })
      .then(({ data }) => {
        const { allSelected } = this.state;
        // If checkbox for all relations is selected, set selected on all new incoming relations
        const newRows = data.data.map(e => ({
          ...e,
          key: `${e.relation.id};${e.invoiceStream.id};${(e.planning && e.planning.id) || ''}`,
          selected: allSelected,
        }));

        this.setState(state => ({
          rows: searchParams.page > 0 ? [...state.rows, ...newRows] : [...newRows],
          total: data.total,
          currentSearchParams: searchParams,
        }));

        return data.data;
      })
      .catch(error => {
        if (!Axios.isCancel(error)) return error;
      });
  };
  handleSelect = ({ key }) => {
    if (!key) return;
    this.setState(({ rows }) => ({
      rows: rows.map(x =>
        x.key === key
          ? {
              ...x,
              selected: !x.selected,
            }
          : x,
      ),
    }));
  };
  handleSelectAll = () => {
    this.setState(({ rows, allSelected }) => ({
      allSelected: !allSelected,
      rows: rows.map(x => (x.selected !== allSelected ? x : { ...x, selected: !allSelected })),
    }));
  };

  isGlobalActionsDisabled = () => {
    const { rows, allSelected } = this.state;
    if (!rows || !rows.length) return true;
    if (allSelected || rows.some(x => x.selected)) return false;

    return true;
  };
  getInvoiceStreamSelection = () => {
    const { rows, allSelected } = this.state;

    // Get all ids that don't match the select all checkbox
    const invoiceStreamIds = rows
      ? rows
          .filter(x => x.selected !== allSelected && x.invoiceStream && x.invoiceStream.id)
          .map(x => x.invoiceStream.id)
      : [];
    return {
      invoiceStreamIds,
      selectAll: allSelected,
    };
  };
  getPlannedRegistrationSelection = () => {
    const { rows, allSelected } = this.state;

    // Get all ids that don't match the select all checkbox
    const ids = rows
      ? rows
          .filter(x => x.selected !== allSelected && x.planning && x.planning.id)
          .map(x => x.planning.id)
      : [];
    return {
      ids,
      selectAll: allSelected,
    };
  };

  getCountSelectedRows() {
    const { allSelected, total, rows } = this.state;

    const count = rows ? rows.filter(x => x.selected !== allSelected).length : 0;

    if (allSelected) {
      return total - count;
    }
    return count;
  }

  getCountSelectedRowsFormatted() {
    const { total } = this.state;
    const { intl } = this.props;
    const count = this.getCountSelectedRows();

    return intl.formatMessage(messages.CountSelected, { count, total });
  }

  getFilterParams = () => {
    const { currentSearchParams, initialSearchParams } = this.state;
    const filters =
      (currentSearchParams && currentSearchParams.overviewFilters) ||
      (initialSearchParams && initialSearchParams.overviewFilters) ||
      {};
    const searchParams = currentSearchParams || initialSearchParams || {};

    return {
      ...filters,
      desc: searchParams.desc,
      sort: searchParams.sortKey,
    };
  };
  batchCreatePlannedRegistrations = model => {
    const bodyParams = this.getInvoiceStreamSelection();
    const urlParams = this.getFilterParams();
    const { stateService, intl } = this.props;
    return api.base
      .post(
        `${config.appUrls.api}/api/PlannedRegistration/BatchCreate`,
        { ...bodyParams, registration: model },
        {
          params: {
            ...urlParams,
          },
        },
      )
      .then(({ data }) => {
        this.setState({ showCreatePlannedRegistrationsModal: false });

        const onClick = data ? () => stateService.gotoJob(data.id) : undefined;

        ToastrService.info(intl.formatMessage(messages.GlobalActionStarted), undefined, onClick);
      })
      .catch(handleHttpError(intl));
  };
  batchRemovePlannedRegistrations = () => {
    const bodyParams = this.getPlannedRegistrationSelection();
    const urlParams = this.getFilterParams();
    const { stateService, intl } = this.props;
    return api.base
      .post(`${config.appUrls.api}/api/PlannedRegistration/BatchRemove`, bodyParams, {
        params: {
          ...urlParams,
        },
      })
      .then(({ data }) => {
        this.setState({ showRemovePlannedRegistrationsModal: false });

        const onClick = data ? () => stateService.gotoJob(data.id) : undefined;

        ToastrService.info(intl.formatMessage(messages.GlobalActionStarted), undefined, onClick);
      })
      .catch(handleHttpError(intl));
  };
  batchChangeUser = model => {
    const bodyParams = { ...this.getPlannedRegistrationSelection(), ...model };
    const urlParams = this.getFilterParams();
    const { stateService, intl } = this.props;
    return api.base
      .post(`${config.appUrls.api}/api/PlannedRegistration/BatchUpdateUser`, bodyParams, {
        params: {
          ...urlParams,
        },
      })
      .then(({ data }) => {
        this.setState({ showChangeUserModal: false });

        const onClick = data ? () => stateService.gotoJob(data.id) : undefined;

        ToastrService.info(intl.formatMessage(messages.GlobalActionStarted), undefined, onClick);
      })
      .catch(handleHttpError(intl));
  };

  getColumns = () => {
    const { taskTemplates, initialSearchParams: { columns = [] } = {} } = this.state;
    return columns.length ? taskTemplates.filter(x => columns.indexOf(x.id) >= 0) : taskTemplates;
  };

  createExcelReport = () => {
    const { intl } = this.props;

    const filterParams = this.getFilterParams();

    const languageCode = intl.locale;

    const urlParams = {
      ...filterParams,
      languageCode,
      connectionId: SignalRService.getConnectionId(),
    };

    this.setState({ creatingReport: true });

    return api.base
      .get(`${config.appUrls.api}/api/PlannedRegistration/Report`, {
        params: urlParams,
      })
      .then(() => {
        ToastrService.info(intl.formatMessage(messages.ExportToExcelStarted));
        this.setState({ creatingReport: false });
      })
      .catch(error => {
        this.setState({ creatingReport: false });
        handleHttpError(intl)(error);
      });
  };

  jobUpdateHandler = job => {
    if (
      (job.jobType === 34 || job.jobType === 35 || job.jobType === 54) &&
      (job.status === 2 || job.status === 3)
    ) {
      this.setState(state => ({
        initialSearchParams: {
          ...state.initialSearchParams,
          page: 0,
          // forces refresh of table
          random: new Date().getTime(),
        },
      }));
    }
  };
  getSelectedRelationLanguages = () => {
    const { allSelected, rows } = this.state;
    return new Promise((resolve, reject) => {
      if (allSelected) {
        const bodyParams = this.getInvoiceStreamSelection();
        const urlParams = this.getFilterParams();
        return api.base
          .post(`${config.appUrls.api}/api/PlannedRegistration/OverviewLanguages`, bodyParams, {
            params: {
              ...urlParams,
            },
          })

          .then(({ data }) => {
            resolve(data);
          })
          .catch(error => {
            reject(error);
          });
      }
      const languages = [
        ...new Set(rows.filter(x => x.selected && !!x.relation).map(x => x.relation.languageCode)),
      ];
      resolve(languages);
    });
  };

  openEmailModal = () => {
    const { intl } = this.props;
    this.getSelectedRelationLanguages()
      .then(languages => {
        this.setState({
          showSendMailModal: true,
          relationLanguages: languages,
        });
      })
      .catch(error => {
        handleHttpError(intl)(error);
      });
  };

  batchSendMail = email => {
    const bodyParams = this.getInvoiceStreamSelection();
    const urlParams = this.getFilterParams();
    const { stateService, intl } = this.props;
    return api.base
      .post(
        `${config.appUrls.api}/api/PlannedRegistration/BatchSendMail`,
        { ...bodyParams, ...email },
        {
          params: {
            ...urlParams,
          },
        },
      )
      .then(({ data }) => {
        this.setState({ showSendMailModal: false });

        const onClick = data ? () => stateService.gotoJob(data.id) : undefined;

        ToastrService.info(intl.formatMessage(messages.GlobalActionStarted), undefined, onClick);
      })
      .catch(handleHttpError(intl));
  };

  batchCreateSingleRegistration = parameters => {
    const bodyParams = this.getInvoiceStreamSelection();
    const urlParams = this.getFilterParams();
    const { stateService, intl } = this.props;
    return api.base
      .post(
        `${config.appUrls.api}/api/PlannedRegistration/BatchSingleRegistration`,
        { ...bodyParams, parameters },
        {
          params: {
            ...urlParams,
          },
        },
      )
      .then(({ data }) => {
        this.setState({ showCreateRegistrationModal: false });

        const onClick = data ? () => stateService.gotoJob(data.id) : undefined;

        ToastrService.info(intl.formatMessage(messages.GlobalActionStarted), undefined, onClick);
      })
      .catch(handleHttpError(intl));
  };

  handleColumnSelection = items => {
    this.setState(({ hideableColumns }) => ({
      ...handleHiddenColumnsSelection(items, hideableColumns, columnSelectionStorageKey),
      showColumnSelectionModal: false,
    }));
  };

  renderRow = item => {
    const { columnStatus } = this.state;
    const { intl } = this.props;
    const { allSelected } = this.state;
    return (
      <PlannedRegistrationOverviewRow
        key={item.key}
        selected={!!item.selected}
        row={item}
        intl={intl}
        handleSelect={this.handleSelect}
        disableSelect={allSelected}
        showPrices={this.hasPriceListPermission}
        columnStatus={columnStatus}
      />
    );
  };
  showAssignments = hasAssignments();

  render() {
    const { intl } = this.props;
    const {
      initialSearchParams,
      rows,
      allSelected,
      showCreatePlannedRegistrationsModal,
      showRemovePlannedRegistrationsModal,
      showSendMailModal,
      showCreateRegistrationModal,
      showChangeUserModal,
      creatingReport,
      relationLanguages,
      showColumnSelectionModal,
      hideableColumns,
      columnStatus,
    } = this.state;

    const hasData = rows && rows.length > 0;

    return (
      <div>
        <SimplePanel
          article="5434794431762"
          title={intl.formatMessage(messages.PlannedRegistrationOverview)}
        >
          <div className="info-block container-fluid">
            <PlannedRegistrationOverviewFilters
              filters={initialSearchParams.overviewFilters}
              onFilterChange={filters => this.filtersChanged(filters)}
              countSelected={this.getCountSelectedRowsFormatted()}
            />
          </div>
        </SimplePanel>
        <div className="row btn-row">
          <div className="col-md-12 pull-right content-right">
            <Button
              btnClassName="btn btn-xs btn-default margin10R"
              onClick={() => this.setState({ showColumnSelectionModal: true })}
            >
              <span className="margin5R">
                <FaCog className="react-icon" />
              </span>
              {intl.formatMessage(messages.ColumnConfig)}
            </Button>
            <Button
              btnClassName="btn btn-xs btn-default margin10R"
              onClick={() => this.createExcelReport()}
              disabled={!hasData || creatingReport}
            >
              <span className="margin5R">
                <FaFileExcel className="file-icon" />
              </span>
              {intl.formatMessage(messages.ExportToExcel)}
            </Button>
            <CanUpdate on="relation">
              <GlobalActionsButton
                onCreatePlannedRegistrations={() =>
                  this.setState({ showCreatePlannedRegistrationsModal: true })
                }
                onRemovePlannedRegistrations={() =>
                  this.setState({ showRemovePlannedRegistrationsModal: true })
                }
                onCreateRegistration={() => this.setState({ showCreateRegistrationModal: true })}
                hasWithoutFilter={
                  initialSearchParams.overviewFilters &&
                  initialSearchParams.overviewFilters.withoutCategoryId
                }
                onChangeUser={() => this.setState({ showChangeUserModal: true })}
                disabled={this.isGlobalActionsDisabled()}
                onSendMail={this.openEmailModal}
              />
            </CanUpdate>
          </div>
        </div>
        <ScrollableTable
          tableClassName="table-grid margin50B"
          loadItems={this.loadItems}
          initialSearchParams={initialSearchParams}
          items={rows}
          renderRow={this.renderRow}
        >
          <ColumnHeader
            title={
              <Checkbox extraClass="margin2L" onClick={this.handleSelectAll} value={allSelected} />
            }
          />
          <ColumnHeader
            title={intl.formatMessage(messages.Relation)}
            sortKey="relation.displayName"
          />
          <ColumnHeader
            title={intl.formatMessage(messages.UniqueIdentifier)}
            sortKey="relation.uniqueidentifier"
            visible={columnStatus.relationUniqueIdentifier}
          />
          <ColumnHeader
            title={intl.formatMessage(messages.RelationCode)}
            sortKey="relation.customerCode,relation.uniqueidentifier"
            visible={columnStatus.relationCode}
          />
          {this.showAssignments ? (
            <ColumnHeader
              title={intl.formatMessage(messages.Assignment)}
              sortKey="assignment.name"
            />
          ) : null}

          <ColumnHeader
            title={intl.formatMessage(messages.Schedule)}
            sortKey="planning.scheduleName"
          />
          <ColumnHeader title={intl.formatMessage(messages.NextPlanningDate)} />
          <ColumnHeader title={intl.formatMessage(messages.PlanningStartStop)} />
          <ColumnHeader title={intl.formatMessage(messages.Resource)} />
          <ColumnHeader title={intl.formatMessage(messages.Quantity)} />
          <ColumnHeader title={intl.formatMessage(messages.User)} visible={columnStatus.user} />
          {this.hasPriceListPermission && (
            <ColumnHeader title={intl.formatMessage(messages.UnitPrice)} />
          )}
          {this.hasPriceListPermission && (
            <ColumnHeader title={intl.formatMessage(messages.Amount)} />
          )}
          <ColumnHeader />
        </ScrollableTable>

        {showColumnSelectionModal && (
          <CheckboxListModal
            title={intl.formatMessage(messages.SelectColumns)}
            items={hideableColumns}
            onSave={this.handleColumnSelection}
            onClose={() => this.setState({ showColumnSelectionModal: false })}
            show={showColumnSelectionModal}
            sortByLabel={false}
          />
        )}

        {showCreatePlannedRegistrationsModal && (
          <CreatePlannedRegistrationsModal
            intl={intl}
            show={showCreatePlannedRegistrationsModal}
            onSave={this.batchCreatePlannedRegistrations}
            onClose={() => this.setState({ showCreatePlannedRegistrationsModal: false })}
            assignmentTemplateId={initialSearchParams.overviewFilters?.assignmentTemplateId}
          />
        )}
        {showRemovePlannedRegistrationsModal && (
          <YesNoModal
            intl={intl}
            show={showRemovePlannedRegistrationsModal}
            title={intl.formatMessage(messages.RemovePlannedRegistrations)}
            message={intl.formatMessage(messages.RemovePlannedRegistrationsConfirmationMsg)}
            onNo={() =>
              this.setState({
                showRemovePlannedRegistrationsModal: false,
              })
            }
            onSave={this.batchRemovePlannedRegistrations}
          />
        )}
        {showSendMailModal && (
          <SendEmailModal
            intl={intl}
            show
            languages={relationLanguages}
            emailContext={13}
            onClose={() =>
              this.setState({
                showSendMailModal: false,
              })
            }
            onSave={emailModel => this.batchSendMail(emailModel)}
          />
        )}
        {showCreateRegistrationModal && (
          <BatchRegistrationModal
            calculateDurationPerRelation={false}
            intl={intl}
            show={showCreateRegistrationModal}
            onClose={() =>
              this.setState({
                showCreateRegistrationModal: false,
              })
            }
            onSave={batchRegistrationModel =>
              this.batchCreateSingleRegistration(batchRegistrationModel)
            }
            crmType={3}
            assignmentTemplateId={initialSearchParams.overviewFilters?.assignmentTemplateId}
          />
        )}
        {showChangeUserModal && (
          <ChangePlannedRegistrationUserModal
            intl={intl}
            show={showChangeUserModal}
            onSave={this.batchChangeUser}
            onClose={() => this.setState({ showChangeUserModal: false })}
          />
        )}
      </div>
    );
  }
}

PlannedRegistrationOverviewContainer.propTypes = {
  intl: intlShape.isRequired,
  stateService: PropTypes.instanceOf(StateService).isRequired,
};

export default showWhenPermission(
  'relation',
  'read',
)(injectStateService(injectIntl(PlannedRegistrationOverviewContainer)));
